/**
 * R6 §5.2 — the one place that decides "is there anything to show this principal?"
 *
 * It sits INSIDE `AppRegistryProvider` (so it can read the visible set from the provider that
 * owns it) and ABOVE `AppHost` (so `AppHost` stays the route table and reads nothing from the
 * auth graph). It could not live in `CpAdminApp`: that component RENDERS
 * `AppRegistryProvider`, so `useAppRegistry()` there throws by construction — and answering the
 * question at that level would mean recomputing visibility a second time, which is a second
 * implementation of "what can this principal see".
 */
import { useMemo } from 'react';
import { useHasPermission } from '@zion/sdk-react';
import { APP_CREATE_PERMISSION } from '../api/apps.api';
import { useAppRegistry } from './AppRegistryContext';
import { AppHost } from './AppHost';
import { useAppsContext } from './apps-context';
import { useGrantsReread } from './grants-context';
import { buildLandingCandidates, resolveLanding, withCreateDoor } from './landing';
import { SectionLayout } from './SectionLayout';
import { ZeroAccessPanel, type GrantsError } from './ZeroAccessPanel';
import { useActiveRole } from './active-role-context';
import { useAccessRequestSummary } from './access-request-summary-context';
import type { ResourceGrantSnapshot } from '@zion/sdk-react';
import type { AvailabilitySummaryState } from '../panes/availability-model';

export interface AuthenticatedShellProps {
  readonly email: string | null;
  /**
   * ⛔ `resourceGrants` and `grantsError` TRAVEL TOGETHER, ALWAYS. Do not split them, and do not
   * let a later tidy-up split them. A `null` with no paired reason is indistinguishable from
   * still-loading: a component receiving one without the other cannot tell "the fetch failed",
   * "the expansion was truncated" and "we haven't asked yet" apart — so it will pick one,
   * silently, and that is the `absent → denied` collapse this release exists to delete.
   *
   * Required, not optional-with-a-default: an optional prop defaulting to `[]` would make every
   * test silently assert the loaded-and-empty case.
   */
  readonly resourceGrants: readonly ResourceGrantSnapshot[] | null;
  readonly grantsError: GrantsError | null;
  readonly onSignOut: () => void;
  /**
   * The fleet-wide availability summary state, forwarded to `AppHost` unchanged (#2621, R-018).
   *
   * ⛔ **THIS COMPONENT TAKES NO DECISION ON IT.** It crosses here because `AuthenticatedShell` is
   * `AppHost`'s only renderer and `AppHost` is where the `railItems` producer lives. The
   * zero-access frames below render no rail and ignore it — which is the property `W4-19` pins.
   *
   * ⛔ Required, not optional-with-a-default, for the reason `resourceGrants` above states.
   */
  readonly availabilitySummary: AvailabilitySummaryState;
}

export function AuthenticatedShell({
  email,
  resourceGrants,
  grantsError,
  onSignOut,
  availabilitySummary,
}: AuthenticatedShellProps): React.JSX.Element {
  const { visibleGlobal, visibleAppScoped } = useAppRegistry();
  const apps = useAppsContext();
  /*
   * ⛔ **READ FROM CONTEXT, WHERE `availabilitySummary` IS A PROP, AND THE ASYMMETRY IS DELIBERATE
   * (#2496, D25).** The access-request value has a second consumer a prop cannot reach —
   * `PeoplePane`, which `AppLoader` builds from a manifest `loader` and hands nothing — so it is in
   * context regardless. Threading it as a prop as well would be two paths to one value, and the
   * next reader would have to work out which one the rail was actually using.
   *
   * ⛔ THIS COMPONENT TAKES NO DECISION ON IT. The state crosses here unchanged, exactly as
   * `availabilitySummary` does, because `AppHost` is where the `railItems` producer lives. ⚠️ The
   * zero-access frames below render no rail and ignore it.
   */
  const accessRequests = useAccessRequestSummary();
  /* Site-wide: `POST /v1/apps` is `@RequirePermission('cp:app:create')`. See `withCreateDoor`. */
  const canCreateApp = useHasPermission(APP_CREATE_PERMISSION);
  /* `null` outside a `GrantsReloadProvider`: no re-read runs there, so none failed. */
  const grantsRereadError = useGrantsReread()?.error ?? null;

  /**
   * ⚠️ Stable across renders, and it has to be for the whole chain or for none of it. Memoising
   * `onSignOut` upstream buys nothing if the object WRAPPING it is rebuilt inline in JSX: an
   * object literal is a fresh identity each render, so `SectionLayout` → `SectionHeader` would
   * see a changed `session` prop on every parent render.
   *
   * Hoisted above the early returns rather than placed next to its use, because hooks must not
   * sit after a conditional return. It is cheap and unused on the zero-access paths.
   */
  const session = useMemo(() => ({ email, onSignOut }), [email, onSignOut]);
  const activeRole = useActiveRole();
  const canSwitchRole =
    activeRole !== null && activeRole.heldSiteRoles.some((role) => role !== activeRole.activeRole);

  /*
   * ⚠️ **THE `sections` MEMO IS GONE (R9).** It existed only for the non-populated frame's HEADER
   * strip — orientation text and nothing more, with `to: '/'` because there was nothing to navigate to
   * on any frame path. The section strip is deleted (sections are rail groups now) and `frame()` passes
   * `showRail={false}`, so this frame has no navigation surface at all — which is correct, and is the
   * whole reason `ZeroAccessPanel` exists: it explains the absence in prose instead.
   */
  /**
   * The non-populated frame. ⛔ `showRail={false}` on EVERY such path: measured on the sibling,
   * an entry-less rail does not degrade to nothing, it degrades to a full-height 240px bordered
   * empty column — the strongest "this is broken, or still loading" signal on the page, which is
   * the exact confusion the zero-access panel exists to remove.
   */
  function frame(
    panelGrants: readonly ResourceGrantSnapshot[] | null,
    panelError: GrantsError | null,
  ): React.JSX.Element {
    return (
      /*
       * The header session only when the person holds a site role that is not active (plan
       * 2026-10-22-cp-active-role, Q2): the account menu then offers the switch to that role.
       * Every other zero-access path keeps no session.
       */
      <SectionLayout showRail={false} session={canSwitchRole ? session : null}>
        <ZeroAccessPanel
          email={email}
          resourceGrants={panelGrants}
          grantsError={panelError}
          onSignOut={onSignOut}
        />
      </SectionLayout>
    );
  }

  /*
   * ⭐ THE ONE QUESTION THIS COMPONENT ANSWERS, and R7's rule for it (§2.3):
   *
   *   > The zero-access panel renders iff the principal has NOWHERE TO LAND.
   *   > The apps list decides only what happens INSIDE the Apps section.
   *
   * R6 asked `apps.apps.length === 0` instead, so an operator holding `cp:tenant:read` and
   * `cp:dp:read` site-wide with zero app grants was told "you don't have access to any sections
   * yet" while Platform was fully visible to them — the `absent → denied` collapse this release
   * exists to delete, one layer above the panel that deletes it.
   *
   * ⚠️ THE BRANCH ORDER BELOW WAS AMENDED BY LEAD RULING (ACK-ws0-1), not by the build. §2.3's
   * table gated the panel on `visibleGlobal.length + visibleAppScoped.length === 0`, and that
   * predicate ships a blank page: a principal holding `cp:app:read` site-wide with ZERO apps has a
   * VISIBLE entry (the catalog) and therefore a non-zero count, but `resolveLanding` correctly
   * answers `none` — and `landingPath({kind:'none'})` is `'/'`, so `AppHost`'s `path="/"` route
   * renders `<Navigate to="/" replace/>` at `/`. Measured on the R6 tree: `<main>` with innerHTML of
   * length **0**, inside fully-rendered chrome, with no loop and no warning — a blank screen nobody
   * would ever file. `landing.kind === 'none'` is the honest predicate, and it makes the blank page
   * unrepresentable rather than merely untested.
   */
  const landing = withCreateDoor(
    resolveLanding(buildLandingCandidates(visibleGlobal, visibleAppScoped), apps.apps),
    canCreateApp,
  );

  /*
   * A failed app fetch, in the shape the panel and the banner both take.
   *
   * ⛔ Tagged `subject: 'apps'` so the card LEADS WITH WHAT FAILED — "We couldn't load your apps",
   * not "We couldn't determine your access". The operator's next question is "what broke?", and the
   * title is where they look; naming the wrong subject sends them to check the wrong thing.
   *
   * ⛔ A GRANTS error WINS: it is the more fundamental failure and its remedy differs.
   */
  /* A failed re-read beside a kept list is a failure too, so the banner names it. */
  const appsFailure = apps.error ?? apps.rereadError;
  const appsError: GrantsError | null =
    appsFailure === null ? null : { kind: 'fetch', message: appsFailure.message, subject: 'apps' };
  /* The same rule for the grants: a failed re-read beside the kept grants is named (R-015 item 4). */
  const failure = grantsError ?? grantsRereadError ?? appsError;

  // ── Order is load-bearing, exactly as it is inside ZeroAccessPanel: every state that could be
  // read as a denial is checked LAST.

  if (landing.kind === 'none') {
    // ⛔ ERRORSTATE, NEVER `denied` (D-13). We do not know what this principal holds, and routing a
    // failed lookup to the "you have no access" card would invent an answer.
    if (failure !== null) return frame(null, failure);
    // Grants still in flight ⇒ the PENDING card. ⛔ Keyed on the GRANTS, not the app list: the app
    // list is irrelevant to whether any section is visible.
    if (resourceGrants === null) return frame(null, null);
    // Loaded, and genuinely nowhere to land. The panel picks its own branch from `resourceGrants`
    // (§6): a tenant/dp-granted operator is told they hold resources but no section; a role-less one
    // is told their account has no roles yet.
    return frame(resourceGrants, null);
  }

  if (landing.kind === 'apps-pending') {
    /*
     * Apps is the leading visible section and its list is not known — the ONLY surviving
     * app-list-driven wait.
     *
     * ⛔ `failure`, not `null`. THIS ARM IS THE ONE §2.3 OMITTED (lead ruling ACK-ws0-1 / G2).
     * `apps-context` reports a FAILED fetch as `{apps: null, error}`, and `resolveLanding` is handed
     * only `apps` — so it is structurally incapable of telling a failure from an in-flight request.
     * Passing `null` here would render "Checking your access…" FOREVER for a permanently-failed
     * fetch, breaking the invariant `apps-context.tsx:5` was written to state: `null` + no error is
     * UNKNOWN; `null` + an error is an ERRORSTATE. They are different answers.
     *
     * ⛔ And with no failure, `null` is passed for BOTH panel props deliberately, even when grants
     * have already arrived as `[]`. Forwarding a loaded-empty `resourceGrants` here would render
     * "you don't have access to any sections yet" — a DENIAL — while the app fetch is still open.
     * Which card shows must reflect what is still unknown, and what is still unknown is the list.
     */
    return frame(null, failure);
  }

  /*
   * ⚠️ `apps.apps` is passed AS-IS, INCLUDING `null`. ⛔ Do not `?? []` it. `AppHost` turns it into
   * a three-state `ScopedAppResolution`; collapsing `null` to `[]` here would make a list that has
   * not arrived indistinguishable from "you can reach no apps", so an app-scoped URL would answer
   * 403 and then flicker into the right screen once the list landed.
   *
   * While a re-read runs or after it failed, the list is the previous usable one and can lack an
   * app the server just made. `appsAbsentDecides` is then `false`: a listed app still opens, and an
   * absent id waits or names the failed read, never a denial.
   */
  return (
    <AppHost
      landing={landing}
      apps={apps.apps}
      appsAbsentDecides={!apps.rereadPending && apps.rereadError === null}
      grantsError={failure}
      session={session}
      availabilitySummary={availabilitySummary}
      accessRequestSummary={accessRequests.state}
    />
  );
}
